import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ShopifyWishlistContextType {
  wishlistCount: number;
  wishlistIds: Set<string>;
  addToWishlist: (productId: string, handle: string) => Promise<boolean>;
  removeFromWishlist: (productId: string) => Promise<boolean>;
  isInWishlist: (productId: string) => boolean;
  refreshWishlist: () => Promise<void>;
}

const ShopifyWishlistContext = createContext<ShopifyWishlistContextType | undefined>(undefined);

export const ShopifyWishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());

  const refreshWishlist = useCallback(async () => {
    if (!user) {
      setWishlistIds(new Set());
      return;
    }

    const { data, error } = await supabase
      .from('shopify_wishlist')
      .select('shopify_product_id')
      .eq('user_id', user.id);

    if (!error && data) {
      setWishlistIds(new Set(data.map(item => item.shopify_product_id)));
    }
  }, [user]);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  const addToWishlist = async (productId: string, handle: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('shopify_wishlist')
      .insert({ user_id: user.id, shopify_product_id: productId, shopify_handle: handle });

    if (error) {
      if (error.code === '23505') return true; // already exists
      return false;
    }

    setWishlistIds(prev => new Set([...prev, productId]));
    return true;
  };

  const removeFromWishlist = async (productId: string): Promise<boolean> => {
    if (!user) return false;

    const { error } = await supabase
      .from('shopify_wishlist')
      .delete()
      .eq('user_id', user.id)
      .eq('shopify_product_id', productId);

    if (error) return false;

    setWishlistIds(prev => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    return true;
  };

  const isInWishlist = (productId: string): boolean => wishlistIds.has(productId);

  return (
    <ShopifyWishlistContext.Provider
      value={{
        wishlistCount: wishlistIds.size,
        wishlistIds,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        refreshWishlist,
      }}
    >
      {children}
    </ShopifyWishlistContext.Provider>
  );
};

export const useShopifyWishlist = () => {
  const context = useContext(ShopifyWishlistContext);
  if (!context) throw new Error('useShopifyWishlist must be used within ShopifyWishlistProvider');
  return context;
};
